import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { QueryClientProvider } from "@tanstack/react-query"
import { Toaster } from "@/components/ui/sonner"
import { queryClient } from "@/lib/queryClient"
import { ConfirmProvider } from "@/components/common/ConfirmDialog"
import { LoginPage } from "@/features/auth/LoginPage"
import { ProtectedRoute } from "@/features/auth/ProtectedRoute"
import { CollabRealtimeProvider } from "@/features/collab/realtime"
import { MainLayout } from "@/components/layout/MainLayout"
import { DashboardPage } from "@/pages/DashboardPage"
import { ComingSoonPage } from "@/pages/ComingSoonPage"
import { MePage } from "@/features/me/MePage"
import { CustomerListPage } from "@/features/customer/CustomerListPage"
import { CustomerDetailPage } from "@/features/customer/CustomerDetailPage"
import { ContractListPage } from "@/features/contract/ContractListPage"
import { ContractDetailPage } from "@/features/contract/ContractDetailPage"
import { TasksPage } from "@/features/tasks/pages/TasksPage"
import { ConfigurationsAdminPage } from "@/features/admin/configurations/ConfigurationsAdminPage"
import { ConfigurationDetailPage } from "@/features/admin/configurations/ConfigurationDetailPage"
import { ProductsAdminPage } from "@/features/admin/products/ProductsAdminPage"
import { ProductDetailPage } from "@/features/admin/products/ProductDetailPage"
import { FormSchemaEditorPage } from "@/features/admin/forms/FormSchemaEditorPage"
import { DictionariesPage } from "@/features/admin/dictionaries/DictionariesPage"
import { StatusTracksAdminPage } from "@/features/admin/status-tracks/StatusTracksAdminPage"
import { RolesAdminPage } from "@/features/admin/roles/RolesAdminPage"
import { RoleDetailPage } from "@/features/admin/roles/RoleDetailPage"
import { TeamsAdminPage } from "@/features/admin/teams/TeamsAdminPage"
import { TeamDetailPage } from "@/features/admin/teams/TeamDetailPage"
import { UsersAdminPage } from "@/features/admin/users/UsersAdminPage"
import { RegionsAdminPage } from "@/features/admin/regions/RegionsAdminPage"
import { AdminPage } from "@/features/admin/AdminPage"
import { NumberingSchemesPage } from "@/features/admin/numbering/NumberingSchemesPage"
import { IntegrationsAdminPage } from "@/features/admin/integrations/IntegrationsAdminPage"
import { NotFoundPage } from "@/pages/NotFoundPage"

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <ProtectedRoute>
                <CollabRealtimeProvider>
                  <MainLayout />
                </CollabRealtimeProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="/me" element={<MePage />} />
            <Route path="/clients" element={<CustomerListPage />} />
            <Route path="/clients/:id" element={<CustomerDetailPage />} />
            <Route path="/contracts" element={<ContractListPage />} />
            <Route path="/contracts/:id" element={<ContractDetailPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/inventory" element={<ComingSoonPage />} />
            <Route path="/logistics" element={<ComingSoonPage />} />
            <Route path="/reports" element={<ComingSoonPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/settings/configurations" element={<ConfigurationsAdminPage />} />
            <Route path="/settings/configurations/:code" element={<ConfigurationDetailPage />} />
            <Route path="/settings/products" element={<ProductsAdminPage />} />
            <Route path="/settings/products/:code" element={<ProductDetailPage />} />
            <Route path="/settings/forms" element={<Navigate to="/settings/forms/contract" replace />} />
            <Route path="/settings/forms/:objectType" element={<FormSchemaEditorPage />} />
            <Route path="/settings/payment-taxonomies" element={<Navigate to="/settings/dictionaries" replace />} />
            <Route path="/settings/dictionaries" element={<DictionariesPage />} />
            <Route path="/settings/status-tracks" element={<StatusTracksAdminPage />} />
            <Route path="/admin/roles" element={<RolesAdminPage />} />
            <Route path="/admin/roles/:code" element={<RoleDetailPage />} />
            <Route path="/admin/teams" element={<TeamsAdminPage />} />
            <Route path="/admin/teams/:id" element={<TeamDetailPage />} />
            <Route path="/admin/users" element={<UsersAdminPage />} />
            <Route path="/admin/regions" element={<RegionsAdminPage />} />
            <Route path="/admin/numbering" element={<NumberingSchemesPage />} />
            <Route path="/admin/integrations" element={<IntegrationsAdminPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      </ConfirmProvider>
      <Toaster />
    </QueryClientProvider>
  )
}

export default App
